import React, { useEffect, useState } from 'react';
import { db } from '../firebase';
import { doc, getDoc, setDoc, collection, addDoc, deleteDoc, onSnapshot } from 'firebase/firestore';
import {
  List, ListItem, ListItemText, Snackbar, ListItemAvatar, Avatar, ListItemSecondaryAction, Box, Button, Typography, Divider, Container, Dialog, DialogTitle, DialogContent, IconButton, Link, useMediaQuery, useTheme
} from '@mui/material';
import { useAuth } from '../auth';
import CloseIcon from '@mui/icons-material/Close';
import CardDetail from './CardDetail'; // Import the CardDetail component
import BundleMarketplaceDetails from './BundleMarketplaceDetails'; // Import the BundleDetails component
import { useAppBarContext } from '../components/AppBarContext'; // Import the AppBarContext
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import PersonIcon from '@mui/icons-material/Person';
import PublicProfile from '../pages/PublicProfile'; // Import PublicProfile component
import Loading from '../components/Loading';
import withAnalytics from '../hoc/withAnalytics';
import MessageBox from '../components/MessageBox';
import { Alert } from '@mui/material';

const currencySymbols = {
  AUD: '$',
  USD: '$',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  // Add more currencies as needed
};

const Cart = () => {
  const { currentUser } = useAuth();
  const { selectedCountry, exchangeRates } = useAppBarContext(); // Use the AppBarContext
  const [cartItems, setCartItems] = useState([]);
  const [usernames, setUsernames] = useState({});
  const [loading, setLoading] = useState(true);
  const [itemDetails, setItemDetails] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [messageBoxOpen, setMessageBoxOpen] = useState(false);
  const [messageBoxTitle, setMessageBoxTitle] = useState('');
  const [messageBoxMessage, setMessageBoxMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [expiredItems, setExpiredItems] = useState([]);

  // REMOVED: useDebounce hook - caused race conditions with stale data
  // Debouncing cart operations leads to multiple overlapping database reads/writes
  // React's automatic state batching is sufficient for UI updates

  const handleRemoveExpiredItem = async (itemToRemove) => {
    try {
      // Remove the selected item from the expired items list
      const updatedExpiredItems = expiredItems.filter(
        (item) =>
          item.docName !== itemToRemove.docName ||
          item.setId !== itemToRemove.setId ||
          item.listingId !== itemToRemove.listingId ||
          item.condition !== itemToRemove.condition
      );
  
      // Update the Firestore document
      await setDoc(doc(db, 'expiredCarts', currentUser.uid), { items: updatedExpiredItems });
      setExpiredItems(updatedExpiredItems);
  
      // Show success message
      setSnackbarMessage('Item removed from expired cart!');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error removing expired cart item:', error);
    }
  };
  

  useEffect(() => {
    const fetchCartItems = async () => {
      if (!currentUser) return;
  
      try {
        // Fetch active cart items
        const cartRef = doc(db, 'carts', currentUser.uid);
        const cartDoc = await getDoc(cartRef);
  
        if (!cartDoc.exists()) {
          await setDoc(cartRef, { items: [] });
        }
        const cartData = cartDoc.exists() ? cartDoc.data() : { items: [] };
        const activeItems = cartData.items || [];
  
        // Fetch expired cart items
        const expiredRef = doc(db, 'expiredCarts', currentUser.uid);
        const expiredDoc = await getDoc(expiredRef);
        const expiredData = expiredDoc.exists() ? expiredDoc.data() : { items: [] };
        const expiredItems = expiredData.items || [];
  
        // Fetch details for active cart items
        const detailedActiveItems = await Promise.all(
          activeItems.map(async (item) => {
            if (!item.setId || !item.docName) {
              console.error('Missing setId or docName:', item);
              return item;
            }
            const cardDoc = await getDoc(doc(db, 'sets', item.setId, 'cards', item.docName));
            return cardDoc.exists() ? { ...item, cardData: cardDoc.data() } : item;
          })
        );
  
        // Fetch details for expired cart items
        const detailedExpiredItems = await Promise.all(
          expiredItems.map(async (item) => {
            if (!item.setId || !item.docName) {
              console.error('Missing setId or docName:', item);
              return item;
            }
            const cardDoc = await getDoc(doc(db, 'sets', item.setId, 'cards', item.docName));
            return cardDoc.exists() ? { ...item, cardData: cardDoc.data() } : item;
          })
        );
  
        setCartItems(detailedActiveItems);
        setExpiredItems(detailedExpiredItems);
  
        // Fetch usernames for both active and expired items
        const sellerIds = Array.from(
          new Set([
            ...detailedActiveItems.map((item) => (item.type === "bundle" ? item.userId : item.sellerId)),
            ...detailedExpiredItems.map((item) => (item.type === "bundle" ? item.userId : item.sellerId)),
          ])
        );
        
        const usernamePromises = sellerIds.map(async (id) => {
          const userDoc = await getDoc(doc(db, "users", id));
          return {
            id,
            username: userDoc.exists() ? userDoc.data().username : id,
            awayStatus: userDoc.exists() ? userDoc.data().awayStatus : false,
          };
        });
        
        const usernameResults = await Promise.all(usernamePromises);
        
        const usernameMap = usernameResults.reduce((acc, { id, username, awayStatus }) => {
          acc[id] = awayStatus ? `${username} (Seller Away)` : username;
          return acc;
        }, {});
        
        setUsernames(usernameMap);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching cart and expired items:", error);
      }
    };
  
    // Real-time listener for active cart items
    const cartRef = doc(db, 'carts', currentUser?.uid);
    const unsubscribeCart = cartRef ? onSnapshot(cartRef, async (snapshot) => {
      if (snapshot.exists()) {
        const cartData = snapshot.data();
        const activeItems = cartData.items || [];
  
        const detailedActiveItems = await Promise.all(
          activeItems.map(async (item) => {
            if (!item.setId || !item.docName) return item;
            const cardDoc = await getDoc(doc(db, 'sets', item.setId, 'cards', item.docName));
            return cardDoc.exists() ? { ...item, cardData: cardDoc.data() } : item;
          })
        );
        setCartItems(detailedActiveItems);
      } else {
        setCartItems([]);
      }
    }) : null;
  
    // Real-time listener for expired cart items
    const expiredRef = doc(db, 'expiredCarts', currentUser?.uid);
    const unsubscribeExpired = expiredRef ? onSnapshot(expiredRef, async (snapshot) => {
      if (snapshot.exists()) {
        const expiredData = snapshot.data();
        const expiredItems = expiredData.items || [];
  
        const detailedExpiredItems = await Promise.all(
          expiredItems.map(async (item) => {
            if (!item.setId || !item.docName) return item;
            const cardDoc = await getDoc(doc(db, 'sets', item.setId, 'cards', item.docName));
            return cardDoc.exists() ? { ...item, cardData: cardDoc.data() } : item;
          })
        );
        setExpiredItems(detailedExpiredItems);
      } else {
        setExpiredItems([]);
      }
    }) : null;
  
    // Fetch initial data
    fetchCartItems();
  
    // Cleanup function to unsubscribe listeners
    return () => {
      if (unsubscribeCart) unsubscribeCart();
      if (unsubscribeExpired) unsubscribeExpired();
    };
  }, [currentUser]);
  
  
  const handleAddToCart = async (item) => {
    try {
      // Add item to active cart
      const updatedCart = [...cartItems, { ...item, addedAt: Date.now() }];
      await setDoc(doc(db, 'carts', currentUser.uid), { items: updatedCart });
      setCartItems(updatedCart);
  
      // Update inventory quantity or delete if quantity is zero
      const listingRef = doc(db, 'sets', item.setId, 'cards', item.docName, 'listings', item.listingId);
      const listingDoc = await getDoc(listingRef);
  
      if (listingDoc.exists()) {
        const condition = item.listing.condition;
        const listingData = listingDoc.data()[condition];
        const updatedQuantity = listingData.quantity - 1;
  
        if (updatedQuantity <= 0) {
          // Delete the listing if no quantity is left
          await deleteDoc(listingRef);
        } else {
          // Update the listing with reduced quantity
          await setDoc(
            listingRef,
            {
              [condition]: {
                ...listingData,
                quantity: updatedQuantity,
              },
            },
            { merge: true }
          );
        }
      }
  
      // Remove item from expired items
      const updatedExpiredItems = expiredItems.filter(
        expiredItem => expiredItem.docName !== item.docName
      );
      await setDoc(doc(db, 'expiredCarts', currentUser.uid), { items: updatedExpiredItems });
      setExpiredItems(updatedExpiredItems);
  
      // Show success message
      setSnackbarMessage('Item re-added to cart!');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error adding item to cart:', error);
    }
  };
  
  

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const handleItemClick = async (item) => {
    try {
      setDialogOpen(true);

      if (item.type === 'listing') {
        if (!item.setId || !item.docName) {
          console.error('Missing setId or docName:', item);
          return;
        }
        const cardDoc = await getDoc(doc(db, 'sets', item.setId, 'cards', item.docName));
        const listingDoc = await getDoc(doc(db, 'sets', item.setId, 'cards', item.docName, 'listings', item.listingId));
        if (cardDoc.exists() && listingDoc.exists()) {
          setItemDetails({ cardData: { ...cardDoc.data(), id: cardDoc.id, setId: item.setId }, listingData: listingDoc.data(), type: 'listing' });
        } else {
          setItemDetails(null);
        }
      } else if (item.type === 'bundle') {
        const bundleRef = doc(db, 'bundlelistings', item.userId, 'bundles', item.id);
        const bundleDoc = await getDoc(bundleRef);
        if (bundleDoc.exists()) {
          setItemDetails({ bundleData: bundleDoc.data(), type: 'bundle' });
        } else {
          setItemDetails(null);
        }
      }
    } catch (error) {
      setItemDetails(null);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setItemDetails(null);
  };

  const handleProfileDialogClose = () => {
    setProfileDialogOpen(false);
    setSelectedUserId(null);
  };

  const handleOpenProfileDialog = (userId) => {
    setSelectedUserId(userId);
    setProfileDialogOpen(true);
  };

  const handleCheckout = async (sellerId) => {
    const sellerItems = cartItems.filter(item => (item.type === 'bundle' ? item.userId : item.sellerId) === sellerId);
  
    const salesRef = collection(db, 'users', sellerId, 'sales');
  
    const saleItems = await Promise.all(sellerItems.map(async item => {
      const cardDoc = await getDoc(doc(db, 'sets', item.setId, 'cards', item.docName));
      const convertedPrice = convertPrice(item.price);
      const staticPrice = `${convertedPrice}`;
  
      // Include the full listing object here
      return {
        docName: item.docName,
        setId: item.setId,
        setName: item.cardData?.set?.name || '',
        listingId: item.listingId,
        quantity: item.quantity,
        price: item.price,
        staticPrice: staticPrice,
        staticQuantity: item.quantity,
        cardName: item.cardData?.name || '',
        cardVersion: item.cardData?.version || '',
        cardImage: item.cardData?.image_uris?.digital?.small || '',
        cardType: item.cardData?.type || '',
        cardRarity: item.cardData?.rarity || '',
        cardInk: item.cardData?.ink || '',
        collector_number: cardDoc.exists() ? cardDoc.data().collector_number : '',
        listing: item.listing, // Include the full listing data here
      };
    }));
  
    const saleDetails = {
      buyerId: currentUser.uid,
      items: saleItems,
      total: saleItems.reduce((sum, item) => sum + (item.staticPrice * item.quantity), 0),
      status: 'Pending',
      timestamp: new Date(),
    };
  
    try {
      // Create a new sale document
      const saleDoc = await addDoc(salesRef, saleDetails);
  
      const purchaseDetails = {
        buyerId: currentUser.uid,
        items: saleItems,
        sellerId: sellerId,
        total: saleDetails.total,
        status: 'Pending',
        timestamp: saleDetails.timestamp,
      };
  
      const purchasesRef = doc(db, 'users', currentUser.uid, 'purchases', saleDoc.id);
      await setDoc(purchasesRef, purchaseDetails);
  
      // Remove items from the cart after checkout
      const remainingItems = cartItems.filter(item => (item.type === 'bundle' ? item.userId : item.sellerId) !== sellerId);
      await setDoc(doc(db, 'carts', currentUser.uid), { items: remainingItems });
      setCartItems(remainingItems);
  
      // Show success message
      setMessageBoxTitle('Checkout Complete');
      setMessageBoxMessage(`Checked out items from seller ${usernames[sellerId] || sellerId}.`);
      setMessageBoxOpen(true);
    } catch (error) {
      console.error('Error during checkout:', error);
      setMessageBoxTitle('Checkout Error');
      setMessageBoxMessage('There was an error during checkout. Please try again.');
      setMessageBoxOpen(true);
    }
  };
  

const handleRemoveItem = async (itemToRemove) => {
  try {
    // BUGFIX: Mark item as restored BEFORE removing from cart
    // This prevents double restoration when cart expires
    const updatedItems = cartItems.map(item => {
      const isItemToRemove = 
        item.docName === itemToRemove.docName && 
        item.setId === itemToRemove.setId && 
        item.listingId === itemToRemove.listingId && 
        (item.condition === itemToRemove.condition || item.listing?.condition === itemToRemove.condition);
      
      if (isItemToRemove) {
        // Mark with restoration timestamp to prevent clearExpiredCarts from restoring again
        return { ...item, restoredAt: Date.now(), restoredBy: 'manual_removal' };
      }
      return item;
    });

    // Filter out the removed item
    const remainingItems = updatedItems.filter(item => 
      !(item.docName === itemToRemove.docName && 
        item.setId === itemToRemove.setId && 
        item.listingId === itemToRemove.listingId && 
        (item.condition === itemToRemove.condition || item.listing?.condition === itemToRemove.condition))
    );

    // First update cart with removal tracking - this is crucial for preventing double restoration
    await setDoc(doc(db, 'carts', currentUser.uid), { items: remainingItems });
    setCartItems(remainingItems);

    // Then restore quantity to listing
    if (itemToRemove.setId && itemToRemove.docName && itemToRemove.listingId) {
      const listingRef = doc(db, 'sets', itemToRemove.setId, 'cards', itemToRemove.docName, 'listings', itemToRemove.listingId);
      const listingDoc = await getDoc(listingRef);

      // Determine condition from either direct property or nested listing object
      const condition = itemToRemove.condition || itemToRemove.listing?.condition;
      
      if (!condition) {
        console.error('[handleRemoveItem] No condition found for item:', itemToRemove);
        return;
      }

      if (listingDoc.exists()) {
        const listingData = listingDoc.data();
        const listingInfo = listingData[condition];

        if (listingInfo) {
          const updatedQuantity = (listingInfo.quantity || 0) + itemToRemove.quantity;
          await setDoc(listingRef, {
            [condition]: {
              ...listingInfo,
              quantity: updatedQuantity,
            },
          }, { merge: true });
          
          console.log(`[handleRemoveItem] Restored ${itemToRemove.quantity} to listing ${itemToRemove.listingId} condition ${condition}`);
        } else {
          // Condition doesn't exist in listing, create it
          await setDoc(listingRef, {
            [condition]: {
              userId: itemToRemove.sellerId || itemToRemove.listing?.userId,
              condition: condition,
              quantity: itemToRemove.quantity,
              price: itemToRemove.price || itemToRemove.listing?.price,
              autoUpdatePrice: itemToRemove.listing?.autoUpdatePrice || false,
              percentage: itemToRemove.listing?.percentage || 100,
            },
          }, { merge: true });
          
          console.log(`[handleRemoveItem] Created condition ${condition} in listing ${itemToRemove.listingId} with quantity ${itemToRemove.quantity}`);
        }
      } else {
        // Listing document doesn't exist, create it with the condition
        await setDoc(listingRef, {
          [condition]: {
            userId: itemToRemove.sellerId || itemToRemove.listing?.userId,
            condition: condition,
            quantity: itemToRemove.quantity,
            price: itemToRemove.price || itemToRemove.listing?.price,
            autoUpdatePrice: itemToRemove.listing?.autoUpdatePrice || false,
            percentage: itemToRemove.listing?.percentage || 100,
          },
        }, { merge: true });
        
        console.log(`[handleRemoveItem] Created new listing ${itemToRemove.listingId} with condition ${condition} and quantity ${itemToRemove.quantity}`);
      }
    }
  } catch (error) {
    console.error("[handleRemoveItem] Error:", error);
    // Show error to user
    setSnackbarMessage('Error removing item from cart');
    setSnackbarOpen(true);
  }
};

const handleQuantityChange = async (item, delta) => {
  try {
    // Prevent rapid-fire clicks from causing issues
    const currentQuantityInCart = item.quantity || 0;
    const newQuantityInCart = Math.max(currentQuantityInCart + delta, 0);
    
    // Don't process if no actual change
    if (newQuantityInCart === currentQuantityInCart) {
      return;
    }

    const listingRef = doc(db, 'sets', item.setId, 'cards', item.docName, 'listings', item.listingId);
    const condition = item.listing.condition;
    
    if (!condition) {
      console.error('[handleQuantityChange] No condition found for item:', item);
      setSnackbarMessage('Error: Invalid item data');
      setSnackbarOpen(true);
      return;
    }
    
    let listingDoc = await getDoc(listingRef);
    let availableStock = 0;

    // Check if listing exists
    if (listingDoc.exists()) {
      const listingData = listingDoc.data();
      const listingInfo = listingData[condition];
      
      if (!listingInfo) {
        console.error(`[handleQuantityChange] Condition ${condition} not found in listing`);
        setSnackbarMessage('Error: Listing condition not found');
        setSnackbarOpen(true);
        return;
      }
      
      availableStock = listingInfo.quantity || 0;
    } else if (delta < 0) {
      // Listing doesn't exist and we're decreasing - this is OK, we'll restore it
      availableStock = 0;
    } else {
      // Listing doesn't exist and we're trying to increase - error
      setSnackbarMessage("No listing available for this item.");
      setSnackbarOpen(true);
      return;
    }

    // Prevent adding more than the available stock
    if (delta > 0 && availableStock < delta) {
      setSnackbarMessage(`Only ${availableStock} more available in stock.`);
      setSnackbarOpen(true);
      return;
    }

    // Update the cart quantity first
    const updatedCartItems = cartItems.map(cartItem =>
      cartItem.docName === item.docName && 
      cartItem.listingId === item.listingId &&
      (cartItem.condition === condition || cartItem.listing?.condition === condition)
        ? { ...cartItem, quantity: newQuantityInCart }
        : cartItem
    ).filter(cartItem => cartItem.quantity > 0);

    await setDoc(doc(db, 'carts', currentUser.uid), { items: updatedCartItems });
    setCartItems(updatedCartItems);

    // Update listing quantity
    if (!listingDoc.exists()) {
      // BUGFIX: Listing was deleted, restore it with the amount we're removing from cart
      // When decreasing from 5->4 (delta=-1), we restore 1 to the listing
      const quantityToRestore = Math.abs(delta);
      
      await setDoc(listingRef, {
        [condition]: {
          userId: item.listing.userId || item.sellerId,
          condition: condition,
          quantity: quantityToRestore,
          price: item.listing.price || item.price,
          autoUpdatePrice: item.listing.autoUpdatePrice || false,
          percentage: item.listing.percentage || 100,
        },
      }, { merge: true });
      
      console.log(`[handleQuantityChange] Restored listing with quantity ${quantityToRestore}`);
    } else {
      // Listing exists, adjust its quantity
      // delta < 0 means removing from cart, so listing increases
      // delta > 0 means adding to cart, so listing decreases
      const newListingQuantity = availableStock - delta;
      
      if (newListingQuantity <= 0) {
        // No stock left, delete the listing
        await deleteDoc(listingRef);
        console.log(`[handleQuantityChange] Deleted listing (no stock remaining)`);
      } else {
        // Update the listing quantity
        const listingData = listingDoc.data();
        const listingInfo = listingData[condition];
        
        await setDoc(listingRef, {
          [condition]: {
            ...listingInfo,
            quantity: newListingQuantity,
          },
        }, { merge: true });
        
        console.log(`[handleQuantityChange] Updated listing quantity from ${availableStock} to ${newListingQuantity}`);
      }
    }
  } catch (error) {
    console.error("[handleQuantityChange] Error:", error);
    setSnackbarMessage('Error updating quantity');
    setSnackbarOpen(true);
  }
};



  // REMOVED: debouncedHandleQuantityChange - now using direct handleQuantityChange calls
  // Debouncing was causing race conditions with stale data

  const convertPrice = (price) => {
    if (!exchangeRates[selectedCountry]) return price;
    return (price * exchangeRates[selectedCountry]).toFixed(2);
  };

  const groupCartItems = (items) => {
    const groupedItems = {};

    items.forEach((item) => {
      const key = `${item.docName}_${item.setId}_${item.listingId}_${item.condition}`;
      if (groupedItems[key]) {
        groupedItems[key].quantity += item.quantity;
      } else {
        groupedItems[key] = { ...item, quantity: item.quantity };
      }
    });

    return Object.values(groupedItems);
  };

  if (loading) {
    return <Loading />;
  }

  const groupedItems = groupCartItems(cartItems).reduce((acc, item) => {
    const sellerKey = item.type === 'bundle' ? item.userId : item.sellerId;
    if (!acc[sellerKey]) {
      acc[sellerKey] = [];
    }
    acc[sellerKey].push(item);
    return acc;
  }, {});

  return (
    <Container>
      <Typography variant="h4" gutterBottom>
        My Cart
      </Typography>
      <Typography variant="h7" gutterBottom>
        Cart will be cleared after 1 hour since last item added.
      </Typography>
  
      {cartItems.length === 0 ? (
        <Typography variant="h6" color="textSecondary">
          Your cart is empty.
        </Typography>
      ) : (
        Object.keys(groupedItems).map((sellerKey) => {
          const sellerItems = groupedItems[sellerKey];
          const total = sellerItems.reduce(
            (sum, item) => sum + item.price * item.quantity,
            0
          );

        return (
          <Box key={sellerKey} mb={4}>
            <Typography variant="h6" gutterBottom>
              Seller:
              <Link
                onClick={() => handleOpenProfileDialog(sellerKey)}
                sx={{ ml: 1 }}
                style={{ cursor: "pointer" }}
              >
                <PersonIcon fontSize="small" />{" "}
                {usernames[sellerKey] || sellerKey}
              </Link>
            </Typography>
            <List>
              {sellerItems.map((item) => (
                <ListItem
                  button
                  onClick={() => handleItemClick(item)}
                  key={`${item.docName}_${item.setId}_${item.listingId}_${item.condition}`}
                  sx={{
                    flexDirection: isMobile ? "column" : "row",
                    alignItems: isMobile ? "flex-start" : "center",
                  }}
                >
                  <ListItemAvatar sx={{ width: 100 }}>
                    <Avatar
                      variant="square"
                      src={
                        item.cardData?.image_uris?.digital?.small ||
                        "fallback_image_url"
                      }
                      sx={{
                        height: "20%",
                        width: "80%",
                        objectFit: "contain",
                        border: `4px solid #000000`,
                      }}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "fallback_image_url";
                      }}
                    />
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      item.type === "bundle"
                        ? item.name
                        : item.cardData?.name
                    }
                    secondary={
                      item.type === "bundle" ? (
                        ""
                      ) : (
                        <React.Fragment>
                          <Typography
                            component="span"
                            variant="body2"
                            display="block"
                            style={{ fontWeight: "bold" }}
                          >
                           {item.version}
                          </Typography>
                          <Typography
                            component="span"
                            variant="body2"
                            display="block"
                          >
                            Card Number: {item.collector_number}
                          </Typography>
                          <Typography
                            component="span"
                            variant="body2"
                            display="block"
                          >
                            Card Type: {item.cardData?.type}
                          </Typography>
                          <Typography
                            component="span"
                            variant="body2"
                            display="block"
                          >
                            Condition: {item.listing.condition}
                          </Typography>
                        </React.Fragment>
                      )
                    }
                  />

                  <ListItemSecondaryAction>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
              <IconButton onClick={() => handleQuantityChange(item, -1)}>
                <RemoveIcon />
              </IconButton>
              <Typography sx={{ mx: 1 }}>{item.quantity}</Typography>
              <IconButton onClick={() => handleQuantityChange(item, 1)}>
                <AddIcon />
              </IconButton>
            </Box>
                    <Typography>{`Price Each: ${
                      currencySymbols[selectedCountry]
                    }${convertPrice(item.price)}`}</Typography>
                    <IconButton
                      edge="end"
                      color="inherit"
                      onClick={() => handleRemoveItem(item)}
                    >

                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
            <Typography variant="h6" gutterBottom>
              Total: {currencySymbols[selectedCountry]}
              {convertPrice(total)}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleCheckout(sellerKey)}
            >
              Checkout Items from {usernames[sellerKey] || sellerKey}
            </Button>
            <Divider sx={{ my: 2 }} />
          </Box>
        );
      })
    )}

{expiredItems.length > 0 && (
  <Box
    sx={{
      mt: 4,
      p: 3,
      border: '2px solid #d32f2f',
      borderRadius: '8px',
      backgroundColor: '#fce4ec',
    }}
  >
    <Typography variant="h5" gutterBottom>
      Expired Cart Items
    </Typography>
    <Typography variant="body2" color="textSecondary" gutterBottom>
      These items were removed due to inactivity. You can re-add them to your cart or remove them permanently.
    </Typography>

    {/* Group expired items by seller */}
    {Object.entries(
      expiredItems.reduce((acc, item) => {
        const sellerKey = item.sellerId || item.userId;
        if (!acc[sellerKey]) acc[sellerKey] = [];
        acc[sellerKey].push(item);
        return acc;
      }, {})
    ).map(([sellerId, items]) => (
      <Box key={sellerId} sx={{ mt: 3 }}>
        {/* Display seller information */}
        <Typography variant="h6" gutterBottom>
          Seller:
          <Link
            onClick={() => handleOpenProfileDialog(sellerId)}
            sx={{ ml: 1 }}
            style={{ cursor: 'pointer' }}
          >
            <PersonIcon fontSize="small" /> {usernames[sellerId] || sellerId}
          </Link>
        </Typography>
        <List>
          {items.map((item) => (
            <ListItem
              key={`${item.docName}_${item.setId}_${item.listingId}_${item.condition}`}
              sx={{
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: isMobile ? 'flex-start' : 'center',
              }}
            >
              <ListItemAvatar sx={{ width: 100 }}>
                <Avatar
                  variant="square"
                  src={item.cardData?.image_uris?.digital?.small || 'fallback_image_url'}
                  sx={{
                    height: '80%',
                    width: '80%',
                    objectFit: 'contain',
                    border: '2px solid #000',
                  }}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'fallback_image_url';
                  }}
                />
              </ListItemAvatar>
              <ListItemText
                primary={item.cardData?.name}
                secondary={
                  <>
                    <Typography variant="body2" display="block" style={{ fontWeight: 'bold' }}>
                      {item.version}
                    </Typography>
                    <Typography variant="body2" display="block">
                      Card Number: {item.collector_number}
                    </Typography>
                    <Typography variant="body2" display="block">
                      Condition: {item.condition}
                    </Typography>
                    <Typography variant="body2" display="block">
                      Price: {currencySymbols[selectedCountry]}{convertPrice(item.price)}
                    </Typography>
                  </>
                }
              />
              <ListItemSecondaryAction>
                {/* Re-add to cart button */}
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => handleAddToCart(item)}
                >
                  Re-add to Cart
                </Button>
                {/* Remove from expired cart button */}
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => handleRemoveExpiredItem(item)}
                  sx={{ ml: 2 }}
                >
                  Remove
                </Button>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      </Box>
    ))}
  </Box>
)}





<Snackbar
  open={snackbarOpen}
  autoHideDuration={3000}
  onClose={handleSnackbarClose}
  anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
  sx={{
    mt: 10, // Adjust this value as needed to set the space from the top
  }}
>
  <Alert onClose={handleSnackbarClose} variant="filled" severity="error">
    {snackbarMessage}
  </Alert>
</Snackbar>


<MessageBox
  open={messageBoxOpen}
  onClose={() => setMessageBoxOpen(false)}
  title={messageBoxTitle}
  message={messageBoxMessage}
/>

      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <IconButton edge="end" color="inherit" onClick={handleDialogClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {itemDetails ? (
            itemDetails.type === 'listing' ? (
              <Box>
                <CardDetail setId={itemDetails.cardData.setId} id={itemDetails.cardData.id} />
                <Typography variant="h6">{`Seller: ${usernames[itemDetails.listingData.sellerId] || itemDetails.listingData.sellerId}`}</Typography>
                <Typography variant="h6">{`Price: ${currencySymbols[selectedCountry]}${convertPrice(itemDetails.listingData.price)}`}</Typography>
              </Box>
            ) : (
              <Box>
                <BundleMarketplaceDetails bundle={itemDetails.bundleData} onClose={handleDialogClose} />
                <Typography variant="h6">{`Seller: ${usernames[itemDetails.bundleData.userId] || itemDetails.bundleData.userId}`}</Typography>
              </Box>
            )
          ) : (
            <Loading />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={profileDialogOpen} onClose={handleProfileDialogClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <IconButton edge="end" color="inherit" onClick={handleProfileDialogClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedUserId && <PublicProfile userId={selectedUserId} />}
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default withAnalytics(Cart);
